import { c as _c } from "react/compiler-runtime";
import isEqual from "lodash/isEqual";
import { useCallback, useState } from "react";

/**
 * A key that changes whenever `content` is replaced by a different document.
 *
 * `EmailComposer` reads `initialContent` once, on mount, so a screen whose saved
 * document can change underneath it — Ask Decoda applying a header from the
 * sidebar — keys the composer with this to remount it on the new content.
 *
 * Only a different document moves the key. `undefined` means no document is
 * known — still loading, or a refetch that failed (`requestData` resolves to
 * null instead of throwing) — and a refetch that brings back an equal document
 * is no change, so neither remounts the editor over unsaved edits.
 *
 * The editor's own Save refetches the same query, and remounting then would
 * throw away anything typed while the save was in flight. Call `expectOwnSave`
 * with the document being saved: the next change of `content` keeps the key if
 * it is that document, and either way the expectation is used up, so a later
 * external change back to the same document still remounts.
 *
 * It holds one expectation at a time, so a save must not settle before its
 * refetch lands — otherwise a second save could replace the first one's
 * expectation. The email save mutations return their invalidation for this.
 */
export function useContentVersion(content) {
  const $ = _c(5);
  let t0;
  if ($[0] !== content) {
    t0 = {
      content,
      version: 0,
      ownSave: undefined
    };
    $[0] = content;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [seen, setSeen] = useState(t0);
  let {
    version
  } = seen;
  if (content !== undefined && seen.content !== content) {
    const unchanged = isEqual(content, seen.content);
    const isOwnSave = !unchanged && seen.ownSave !== undefined && isEqual(content, seen.ownSave);
    version = unchanged || isOwnSave ? seen.version : seen.version + 1;
    setSeen({
      content,
      version,
      ownSave: unchanged ? seen.ownSave : undefined
    });
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = saved => {
      setSeen(current => ({
        ...current,
        ownSave: saved
      }));
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const expectOwnSave = t1;
  let t2;
  if ($[3] !== version) {
    t2 = {
      version,
      expectOwnSave
    };
    $[3] = version;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}